import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon, CheckCircleRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const SubscriberWelcomeDialog = ({ open, onClose, userData }) => {
  const { t } = useTranslation("home");

  // Each membership's name (CareMap Plus, HealthDesk Member, HealthDesk Plus) is
  // written into its own sentence, so a language can inflect it.
  const getWelcomeMessage = (role) => {
    if (role === "facility") return t("subscriberWelcome.message.facility");
    if (role === "patient") return t("subscriberWelcome.message.patient");
    return t("subscriberWelcome.message.other");
  };

  const getFeatures = (role) => {
    if (role === "facility") {
      return [
        t("subscriberWelcome.features.facilitySchedule"),
        // TODO: Re-enable once Twilio BAA is in place
        // "Manage your virtual queue",
        // "Send your patients a text when you're ready to see them",
      ];
    }

    if (role === "patient") {
      return [
        t("subscriberWelcome.features.unlimitedMessages"),
        t("subscriberWelcome.features.adFree"),
        t("subscriberWelcome.features.medicalLibrary"),
      ];
    }

    if (role === "professional") {
      return [
        t("subscriberWelcome.features.unlimitedMessages"),
        t("subscriberWelcome.features.adFree"),
        t("subscriberWelcome.features.unlimitedAccess", { name: "BrainFlash" }),
        t("subscriberWelcome.features.unlimitedAccess", { name: "DeepDive" }),
        t("subscriberWelcome.features.unlimitedAccess", { name: "PeerView" }),
      ];
    }

    return [
      t("subscriberWelcome.features.unlimitedMessages"),
      t("subscriberWelcome.features.adFree"),
      t("subscriberWelcome.features.allFeatures"),
    ];
  };

  const getButtonText = (role) => {
    if (role === "facility") {
      return t("subscriberWelcome.button.facility");
    }
    return t("subscriberWelcome.button.other");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: "hidden" },
      }}
    >
      <IconButton
        aria-label={t("common.closeAria")}
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
          zIndex: 1,
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ textAlign: "center", p: 4, pb: 2 }}>
        <Box sx={{ mb: 3 }}>
          <CheckCircleRounded
            sx={{
              fontSize: 64,
              color: "primary.main",
              mb: 2,
              display: "block",
              mx: "auto",
            }}
          />
        </Box>

        <Typography variant="h4" sx={{ mb: 2 }}>
          {t("subscriberWelcome.title")}
        </Typography>

        <Typography variant="h6" sx={{ mb: 4, color: "text.secondary" }}>
          {getWelcomeMessage(userData?.role)}
        </Typography>

        <Box
          sx={{
            borderRadius: 2,
            px: 3,
            pb: 1,
            mb: 2,
            textAlign: "left",
          }}
        >
          {getFeatures(userData?.role).map((feature, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", mb: 1 }}
            >
              <CheckCircleRounded
                sx={{ color: "primary.main", fontSize: 20, mr: 1.5 }}
              />
              <Typography variant="body1">{feature}</Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", p: 3, pt: 1 }}>
        <Button
          variant="contained"
          size="large"
          onClick={onClose}
          sx={{ px: 4, py: 1.5 }}
        >
          {getButtonText(userData?.role)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriberWelcomeDialog;
